import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "./CookieSettings.scss";

const defaultConsent = {
    necessary: true,
    googleMaps: false,
    instagram: false,
    tracking: false,
    marketing: false,
};

const CookieSettings = () => {
    const { t } = useTranslation();

    const [settings, setSettings] = useState(defaultConsent);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const loadSettings = () => {
            const savedConsent =
                localStorage.getItem("cookieConsent");

            if (!savedConsent) {
                setSettings(defaultConsent);
                return;
            }

            try {
                const parsedConsent =
                    JSON.parse(savedConsent);

                setSettings({
                    ...defaultConsent,
                    ...parsedConsent,
                    necessary: true,
                });
            } catch (error) {
                console.error(
                    "Cookie settings error:",
                    error
                );

                setSettings(defaultConsent);
            }
        };

        loadSettings();
    }, []);

    const handleChange = (category) => {
        setSettings((previous) => ({
            ...previous,
            [category]: !previous[category],
        }));

        setSaved(false);
    };

    const saveSettings = () => {
        const consent = {
            ...settings,
            necessary: true,
        };

        localStorage.setItem(
            "cookieConsent",
            JSON.stringify(consent)
        );

        window.dispatchEvent(
            new Event("cookieConsentChanged")
        );

        setSettings(consent);
        setSaved(true);
    };

    const acceptAll = () => {
        const consent = {
            necessary: true,
            googleMaps: true,
            instagram: true,
            tracking: true,
            marketing: true,
        };

        localStorage.setItem(
            "cookieConsent",
            JSON.stringify(consent)
        );

        setSettings(consent);

        window.dispatchEvent(
            new Event("cookieConsentChanged")
        );

        setSaved(true);
    };

    const rejectAll = () => {
        const consent = {
            necessary: true,
            googleMaps: false,
            instagram: false,
            tracking: false,
            marketing: false,
        };

        localStorage.setItem(
            "cookieConsent",
            JSON.stringify(consent)
        );

        setSettings(consent);

        window.dispatchEvent(
            new Event("cookieConsentChanged")
        );

        setSaved(true);
    };

    return (
        <section className="cookie-settings-page">
            <div className="cookie-settings-page__container">

                <h1>
                    {t("cookies.title")}
                </h1>

                <p className="cookie-settings-page__intro">
                    {t("cookies.description")}
                </p>

                {/* NOTWENDIGE */}

                <div className="cookie-settings-page__item">
                    <div>
                        <h2>
                            {t(
                                "cookies.settings.necessary.title"
                            )}
                        </h2>

                        <p>
                            {t(
                                "cookies.settings.necessary.description"
                            )}
                        </p>
                    </div>

                    <span className="cookie-settings-page__required">
                        {t(
                            "cookies.settings.alwaysActive"
                        )}
                    </span>
                </div>

                {/* GOOGLE MAPS */}

                <div className="cookie-settings-page__item">
                    <div>
                        <h2>
                            {t(
                                "cookies.settings.googleMaps.title"
                            )}
                        </h2>

                        <p>
                            {t(
                                "cookies.settings.googleMaps.description"
                            )}
                        </p>
                    </div>

                    <label className="cookie-switch">
                        <input
                            type="checkbox"
                            checked={settings.googleMaps}
                            onChange={() =>
                                handleChange("googleMaps")
                            }
                        />

                        <span className="cookie-switch__slider"></span>
                    </label>
                </div>

                {/* INSTAGRAM */}

                <div className="cookie-settings-page__item">
                    <div>
                        <h2>
                            {t(
                                "cookies.settings.instagram.title"
                            )}
                        </h2>

                        <p>
                            {t(
                                "cookies.settings.instagram.description"
                            )}
                        </p>
                    </div>

                    <label className="cookie-switch">
                        <input
                            type="checkbox"
                            checked={settings.instagram}
                            onChange={() =>
                                handleChange("instagram")
                            }
                        />

                        <span className="cookie-switch__slider"></span>
                    </label>
                </div>

                {/* TRACKING */}

                <div className="cookie-settings-page__item">
                    <div>
                        <h2>
                            {t(
                                "cookies.settings.tracking.title"
                            )}
                        </h2>

                        <p>
                            {t(
                                "cookies.settings.tracking.description"
                            )}
                        </p>
                    </div>

                    <label className="cookie-switch">
                        <input
                            type="checkbox"
                            checked={settings.tracking}
                            onChange={() =>
                                handleChange("tracking")
                            }
                        />

                        <span className="cookie-switch__slider"></span>
                    </label>
                </div>

                {/* MARKETING */}

                <div className="cookie-settings-page__item">
                    <div>
                        <h2>
                            {t(
                                "cookies.settings.marketing.title"
                            )}
                        </h2>

                        <p>
                            {t(
                                "cookies.settings.marketing.description"
                            )}
                        </p>
                    </div>

                    <label className="cookie-switch">
                        <input
                            type="checkbox"
                            checked={settings.marketing}
                            onChange={() =>
                                handleChange("marketing")
                            }
                        />

                        <span className="cookie-switch__slider"></span>
                    </label>
                </div>

                {/* BUTTONS */}

                <div className="cookie-settings-page__buttons">

                    <button
                        type="button"
                        className="cookie-settings-page__button cookie-settings-page__button--secondary"
                        onClick={rejectAll}
                    >
                        {t(
                            "cookies.buttons.necessary"
                        )}
                    </button>

                    <button
                        type="button"
                        className="cookie-settings-page__button"
                        onClick={saveSettings}
                    >
                        {t(
                            "cookies.buttons.save"
                        )}
                    </button>

                    <button
                        type="button"
                        className="cookie-settings-page__button cookie-settings-page__button--secondary"
                        onClick={acceptAll}
                    >
                        {t(
                            "cookies.buttons.acceptAll"
                        )}
                    </button>

                </div>

                {saved && (
                    <p className="cookie-settings-page__saved">
                        {t(
                            "cookies.saved"
                        )}
                    </p>
                )}

            </div>
        </section>
    );
};

export default CookieSettings;